import React, { useCallback, useEffect, useState } from "react";
import sparkles from "../../img/sparkles.svg";
import apiService from "../../services/api";

const ChatHeader = ({ leadId }) => {
  const [nextAction, setNextAction] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [insights, setInsights] = useState<Record<string, any>>({});

  const formatNextAction = useCallback((val?: string) => {
    if (!val) return "";
    const map: Record<string, string> = {
      ASK_MOTIVE: "Ask for motive",
      ASK_BUYER_TYPE: "Ask for Buy and/or sell",
      ASK_TIMEFRAME: "Ask for timeframe",
      ASK_AGENT_STATUS: "Ask for agent status",
      ASK_FIRST_TIME_BUYER: "Ask for first-time homebuyer",
      ASK_INCOME: "Ask for income",
      ASK_BUDGET: "Ask for budget/home value",
      ASK_CREDIT_SCORE: "Ask for credit score",
      ASK_CASH_OR_MORTGAGE: "Ask for cash or mortgage",
      ASK_EMPLOYMENT: "Ask for employment",
      ASK_OTHER_HOMES: "Ask for other homes",
      ASK_LOCATION: "Ask for location",
      ASK_PRICE_RANGE: "Ask for price range",
      SCHEDULE_CALL: "Schedule a call",
      SEND_LISTINGS: "Send listings",
      FOLLOW_UP: "Follow up",
    };
    if (map[val]) return map[val];
    return val.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase());
  }, []);

  // Helper function to check if a field has a value
  const hasFieldValue = useCallback((insightsData: Record<string, any>, field: string): boolean => {
    const value = insightsData[field];
    if (typeof value === 'boolean') return true;
    return Boolean(value !== undefined && value !== null && value !== '' && String(value).trim());
  }, []);

  // Determine next action based on filled fields (checking all fields in order until "Other Homes")
  const determineNextAction = useCallback((insightsData: Record<string, any>): string => {
    // Define fields in priority order (matching LeadContent informationSections)
    const fieldChecks: Array<{ field: string; action: string }> = [
      { field: 'motive', action: 'ASK_MOTIVE' },
      { field: 'buyOrSell', action: 'ASK_BUYER_TYPE' },
      { field: 'timeframe', action: 'ASK_TIMEFRAME' },
      { field: 'agentStatus', action: 'ASK_AGENT_STATUS' },
      { field: 'firstTimeBuyer', action: 'ASK_FIRST_TIME_BUYER' },
      { field: 'income', action: 'ASK_INCOME' },
      { field: 'homeValue', action: 'ASK_BUDGET' },
      { field: 'creditScore', action: 'ASK_CREDIT_SCORE' },
      { field: 'cashOrMortgage', action: 'ASK_CASH_OR_MORTGAGE' },
      { field: 'employment', action: 'ASK_EMPLOYMENT' },
      { field: 'otherHomes', action: 'ASK_OTHER_HOMES' },
    ];

    // Find the first field that doesn't have a value
    for (const { field, action } of fieldChecks) {
      if (!hasFieldValue(insightsData, field)) {
        return action;
      }
    }

    // If all fields are filled, fall back to API response or default
    return '';
  }, [hasFieldValue]);

  useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      if (!leadId) return;
      try {
        setLoading(true);
        
        // Fetch insights and next best action in parallel
        const [fetchedInsights, apiAction] = await Promise.all([
          apiService.getLeadInsights(String(leadId)).catch(() => ({})),
          apiService.getNextBestAction(String(leadId)).catch(() => ""),
        ]);
        
        if (cancelled) return;
        
        const insightsData = fetchedInsights || {};
        setInsights(insightsData);
        
        // Determine next action based on filled fields (overrides API response)
        const determinedAction = determineNextAction(insightsData);
        const finalAction = determinedAction || apiAction || "";
        
        if (!cancelled) setNextAction(finalAction);
      } catch (e) {
        if (!cancelled) {
          setNextAction("");
          console.warn("Failed to load Next Best Action", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    fetchData();
    
    return () => { cancelled = true; };
  }, [leadId, determineNextAction]);

  // Update action when insights change (for real-time updates)
  useEffect(() => {
    if (Object.keys(insights).length > 0) {
      const determinedAction = determineNextAction(insights);
      if (determinedAction) {
        setNextAction(determinedAction);
      }
    }
  }, [insights, determineNextAction]);

  return (
    <div className="flex items-center justify-between h-[56px] px-6 py-4 border-b border-lighterGray">
      <div className="flex items-center gap-x-2">
        <p className="text-h3 text-darkestSlate">Next Best Action:</p>
        <img src={sparkles} alt="sparkles" className="w-[16px]" />
        <div className="font-semibold text-darkBlue">
          {loading ? "Loading..." : (formatNextAction(nextAction) || "—")}
        </div>
      </div>
      {/* Auto-refresh is enabled elsewhere; manual refresh removed as requested */}
    </div>
  );
};

export default ChatHeader;
